import { useMemo } from 'react';

import { isAfter, isEqual, parse } from 'date-fns';

import { filterMap } from 'utils/array';
import { getAllFifteenMinsInADay } from 'utils/dateTime';

import { ITimeOptions, IUseTimeOptionsArgs } from './types';

export const START_CATERING_PICKUP_HOUR = 11; // 11 am
export const END_CATERING_PICKUP_HOUR = 21; // 9 pm

/**
 * Given a `date`, this hook will return an array of times that a user can
 * select for their catering pick up order.
 *
 * For TH, this means any 15min increments that are >= 23hrs from the current
 * date.
 */
export const useTimeOptions = ({
  date,
  firstAvailableTime,
  now,
  nowPlusOffsetHours,
}: IUseTimeOptionsArgs): ITimeOptions[] => {
  const timeOptions = useMemo(() => {
    const allFifteenMinuteIncrementsInADay = getAllFifteenMinsInADay();

    return filterMap<string, ITimeOptions>(
      timeIncrement => {
        // if the current day selected is the next day we need to remove the options
        // that would allow for a dateTime < 25hrs
        return (
          isEqual(
            parse(`${date} ${timeIncrement}`, 'yyyy-MM-dd h:mm aaaa', now),
            parse(firstAvailableTime, 'h:mm aaaa', nowPlusOffsetHours)
          ) ||
          isAfter(
            parse(`${date} ${timeIncrement}`, 'yyyy-MM-dd h:mm aaaa', now),
            parse(firstAvailableTime, 'h:mm aaaa', nowPlusOffsetHours)
          )
        );
      },
      time => ({ value: time, label: time })
    )(allFifteenMinuteIncrementsInADay);
  }, [date, now, nowPlusOffsetHours, firstAvailableTime]);

  return [{ value: '', label: '--' }, ...timeOptions];
};

export default useTimeOptions;
